import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Space, List, message, Typography, Select, Card, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { SharedStep, SharedStepCreate, SharedStepUpdate, Step } from '../../types/sharedStep';
import { Folder } from '../../types/testItem';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: SharedStepCreate | SharedStepUpdate) => void;
    projectId: number;
    folderId?: number;
    sharedStep?: SharedStep;
    folders: Folder[];
    currentFolderId?: number;
}

export const SharedStepModal: React.FC<Props> = ({
    open,
    onClose,
    onSubmit,
    projectId,
    folderId,
    sharedStep,
    folders,
    currentFolderId,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [form] = Form.useForm();
    const [steps, setSteps] = useState<Step[]>([]);
    const [currentStep, setCurrentStep] = useState<Step>({
        action: '',
        result: {
            expected: '',
            data: {
                test_data: '',
                attachment_ids: [],
                test_data_ids: []
            }
        }
    });

    useEffect(() => {
        if (sharedStep) {
            form.setFieldsValue({
                name: sharedStep.name,
                description: sharedStep.description,
                folder_id: sharedStep.folder_id || currentFolderId,
                project_id: projectId,
            });
            setSteps(sharedStep.steps);
        } else {
            form.resetFields();
            form.setFieldsValue({
                folder_id: currentFolderId,
                project_id: projectId,
            });
            setSteps([]);
        }
    }, [sharedStep, form, currentFolderId, projectId]);

    const handleAddStep = () => {
        if (!currentStep.action || !currentStep.result?.expected) {
            message.warning(t('sharedSteps.notifications.validation.fillAllFields'));
            return;
        }

        setSteps([...steps, currentStep]);
        setCurrentStep({
            action: '',
            result: {
                expected: '',
                data: {
                    test_data: '',
                    attachment_ids: [],
                    test_data_ids: []
                }
            }
        });
    };

    const handleRemoveStep = (index: number) => {
        const newSteps = [...steps];
        newSteps.splice(index, 1);
        setSteps(newSteps);
    };

    const validateStep = (step: Step) => {
        if (!step.action || !step.result?.expected) {
            message.warning(t('sharedSteps.notifications.validation.fillAllFields'));
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (steps.length === 0) {
                message.warning(t('sharedSteps.notifications.validation.addAtLeastOne'));
                return;
            }

            const formData = {
                ...values,
                project_id: projectId,
                steps: steps,
                created_by: user?.id
            };

            await onSubmit(formData);
            message.success(t('sharedSteps.notifications.addSuccess'));
            onClose();
        } catch (error) {
            console.error('Error saving shared step:', error);
            message.error(t('sharedSteps.notifications.saveError'));
        }
    };

    return (
        <Modal
            title={
                <Space>
                    {sharedStep ? <EditOutlined /> : <PlusOutlined />}
                    <span>{sharedStep ? 'Редактировать общий шаг' : 'Создать общий шаг'}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Отмена
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                    icon={<SaveOutlined />}
                >
                    {sharedStep ? 'Сохранить' : 'Создать'}
                </Button>
            ]}
            width={500}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    name: sharedStep?.name || '',
                    description: sharedStep?.description || '',
                    folder_id: sharedStep?.folder_id || currentFolderId,
                    project_id: projectId,
                }}
            >
                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{ required: true, message: 'Пожалуйста, введите название' }]}
                >
                    <Input placeholder="Введите название шага" />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                >
                    <Input.TextArea 
                        placeholder="Введите описание шага (необязательно)"
                        rows={2}
                    />
                </Form.Item>

                <Form.Item
                    name="folder_id"
                    label="Папка"
                >
                    <Select 
                        placeholder="Выберите папку"
                        allowClear
                    >
                        {folders.map(folder => (
                            <Select.Option key={folder.id} value={folder.id}>
                                {folder.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <div style={{ marginBottom: '16px' }}>
                    <Typography.Text strong>Добавить шаг</Typography.Text>
                    <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
                        <Input.TextArea
                            value={currentStep.action}
                            onChange={e => setCurrentStep({
                                ...currentStep,
                                action: e.target.value
                            })}
                            placeholder="Действие"
                            rows={2}
                            style={{ width: '100%' }}
                        />
                        <Input.TextArea
                            value={currentStep.result?.expected}
                            onChange={e => setCurrentStep({
                                ...currentStep,
                                result: {
                                    ...currentStep.result,
                                    expected: e.target.value
                                }
                            })}
                            placeholder="Ожидаемый результат"
                            rows={2}
                            style={{ width: '100%' }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddStep}
                            style={{ alignSelf: 'flex-start' }}
                        >
                            Добавить шаг
                        </Button>
                    </Space>
                </div>

                <div>
                    <Typography.Text strong>Список шагов</Typography.Text>
                    {steps.length === 0 ? (
                        <Empty
                            description="Добавьте шаги"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ margin: '16px 0' }}
                        />
                    ) : (
                        <List
                            dataSource={steps}
                            renderItem={(step, index) => (
                                <List.Item
                                    style={{ padding: '8px 0' }}
                                    actions={[
                                        <Button
                                            type="text"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleRemoveStep(index)}
                                        />
                                    ]}
                                >
                                    <div style={{ width: '100%' }}>
                                        <Typography.Text strong>
                                            Шаг {index + 1}: {step.action}
                                        </Typography.Text>
                                        <Typography.Paragraph style={{ margin: '4px 0 0 0' }}>
                                            Ожидаемый результат: {step.result?.expected}
                                        </Typography.Paragraph>
                                    </div>
                                </List.Item>
                            )}
                        />
                    )}
                </div>
            </Form>
        </Modal>
    );
}; 